import axios from 'axios';
import { getApiBase } from './getApiBase';

/**
 * Wires the default axios instance to satisfy the server-side
 * conditionalCsrfProtection middleware (server/src/middleware/csrfMiddleware.js):
 *   - lazily fetches a token from GET /api/csrf-token (also sets the XSRF-TOKEN cookie)
 *   - attaches it as `x-csrf-token` on POST/PUT/PATCH/DELETE
 *   - on a 403, clears the cached token, refetches once, and retries the original request
 */

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

const resolveApiOrigin = () => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
      return `${protocol}://localhost:5002`;
    }
  }
  return String(getApiBase() || '').replace(/\/+$/, '');
};

let cachedToken = null;
let inFlight = null;
let installed = false;

const fetchToken = () => {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      const url = `${resolveApiOrigin()}/api/csrf-token`;
      const res = await axios.get(url, {
        withCredentials: true,
        __csrfBootstrap: true,
      });
      cachedToken = res?.data?.csrfToken || null;
      return cachedToken;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
};

export const resetCsrfToken = () => {
  cachedToken = null;
};

export const ensureCsrfToken = async () => {
  if (!cachedToken) await fetchToken();
  return cachedToken;
};

export const installCsrfInterceptors = () => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use(async (config) => {
    if (config && config.__csrfBootstrap) return config;
    if (config && config.withCredentials === undefined) config.withCredentials = true;

    const method = String(config?.method || 'get').toLowerCase();
    if (!MUTATING_METHODS.has(method)) return config;

    if (!cachedToken) {
      try { await fetchToken(); } catch (_) { /* swallow — request will 403 and trigger retry */ }
    }
    if (cachedToken) {
      config.headers = config.headers || {};
      config.headers['x-csrf-token'] = cachedToken;
    }
    return config;
  });

  axios.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const original = error?.config;
      const status = error?.response?.status;
      if (status !== 403 || !original || original.__csrfRetried || original.__csrfBootstrap) {
        return Promise.reject(error);
      }
      const method = String(original.method || 'get').toLowerCase();
      if (!MUTATING_METHODS.has(method)) return Promise.reject(error);

      original.__csrfRetried = true;
      cachedToken = null;
      try {
        await fetchToken();
        if (!cachedToken) return Promise.reject(error);
        original.headers = original.headers || {};
        original.headers['x-csrf-token'] = cachedToken;
        return axios.request(original);
      } catch (_) {
        return Promise.reject(error);
      }
    }
  );
};

installCsrfInterceptors();
fetchToken().catch(() => { /* best-effort prefetch */ });

export default { ensureCsrfToken, resetCsrfToken, installCsrfInterceptors };
